import { useState, useEffect, useCallback } from "react";
import { Loader2, Receipt, FileText, Send, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errMsg } from "@/components/webadvisor/LeadDetails";
import { ScanQuoteButton } from "@/components/quotes/ScanQuote";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const QuickQuotes = ({ leadId, isAdmin, isCustomer, onSent }) => {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get(`/api/webadvisor/leads/${leadId}/quick-quotes`).then(r => setList(r.data)).catch(() => {}), [leadId]);
  useEffect(() => { load(); }, [load]);

  const create = async (kind) => {
    setBusy(kind);
    const tab = window.open("", "_blank");
    try {
      const { data } = await api.post(`/api/webadvisor/leads/${leadId}/instant-quote`, { kind, origin_url: window.location.origin });
      toast.success(`${kind === "quote" ? "Quote" : "Invoice"} created from the audit — edit the lines, then Send to portal`);
      if (tab) tab.location.href = `/invoice/${data.share_token}`;
      load();
    } catch (e) { tab?.close(); toast.error(errMsg(e, "Couldn't create it")); } finally { setBusy(""); }
  };
  const send = async (q) => {
    setBusy(q.plan_id);
    try {
      const { data } = await api.post(`/api/webadvisor/plan/${q.plan_id}/send-quick`, { notify: true, origin_url: window.location.origin });
      data.email_warning ? toast.warning(data.email_warning) : toast.success(`Sent — it's in their portal under ${q.kind === "quote" ? "Quotes" : "Billing"}`);
      load(); onSent?.();
    } catch (e) { toast.error(errMsg(e, "Couldn't send")); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-3" data-testid="quick-quotes">
      {isAdmin && (
        <div className="flex flex-wrap gap-2">
          <ScanQuoteButton refq={{ lead_id: leadId }} onDone={load} label="Scan website & instant quote" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold px-4 py-2 rounded-lg" />
          <button onClick={() => create("quote")} disabled={!!busy} className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-700 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50" data-testid="instant-quote-btn">{busy === "quote" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}Convert audit to quote</button>
          <button onClick={() => create("invoice")} disabled={!!busy} className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50" data-testid="instant-invoice-btn">{busy === "invoice" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}Convert audit to invoice</button>
        </div>
      )}
      <p className="text-xs text-slate-500">Each critical/high fix becomes a line ($250 / $150), medium fixes are grouped ($75 each). Edit any line before sending. Quotes are signed then paid; invoices are paid right away.</p>
      {list.length > 0 && (
        <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
          {list.map(q => (
            <div key={q.plan_id} className="flex flex-wrap items-center gap-2 px-3 py-2.5" data-testid={`quick-row-${q.plan_id}`}>
              <div className="flex-1 min-w-[180px]">
                <p className="text-sm font-semibold text-slate-900">{q.kind === "quote" ? "Quote" : "Invoice"} {q.invoice_number} · {money(q.total)}</p>
                <p className="text-xs text-slate-500">{q.created_at.slice(0, 10)} · {q.sent_at ? "sent" : "not sent yet"}{q.signed ? " · signed" : ""}{q.paid > 0 ? ` · paid ${money(q.paid)}` : ""}</p>
              </div>
              <a href={`/invoice/${q.share_token}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline" data-testid={`quick-edit-${q.plan_id}`}><Pencil className="w-3 h-3" />{isAdmin ? "View / edit lines" : "View"}</a>
              <button onClick={() => send(q)} disabled={!!busy || !isCustomer} title={isCustomer ? "" : "Turn this lead into a customer first"} className="inline-flex items-center gap-1 text-xs font-bold bg-green-600 hover:bg-green-500 text-white px-3 py-1.5 rounded-lg disabled:opacity-40" data-testid={`quick-send-${q.plan_id}`}>
                {busy === q.plan_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}{q.sent_at ? "Resend" : "Send to portal"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
